/**
 * ProjectPicker - DB-backed project selector
 *
 * Projects are now persisted in the database via the projects API.
 * On first load, migrates any workspace-state projects to the DB.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useProject, type Project } from '../../contexts/ProjectContext';
import { useBufferSync } from '../../contexts/BufferSyncContext';
import { useApi } from '../../contexts/ApiContext';

export function ProjectPicker() {
  const {
    projects,
    activeProject,
    activeProjectId,
    setActiveProject,
    createProject,
    deleteProject,
    refreshProjects,
  } = useProject();
  const { sessionId, buffers, switchBuffer } = useBufferSync();
  const api = useApi();

  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [migrated, setMigrated] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // ELECTRON: Skip cloud API calls
  const isElectron = typeof window !== 'undefined' && !!window.isElectron;

  // One-time migration from workspace-state projects to DB (cloud only)
  useEffect(() => {
    // ELECTRON: Skip migration - no cloud workspace state
    if (isElectron) return;
    if (migrated) return;
    setMigrated(true);

    api.workspaceState.get('projects').then(async ({ value }) => {
      if (!Array.isArray(value) || value.length === 0) return;

      // Migrate each project to DB
      for (const old of value as Array<{ name: string; description?: string; sessionId?: string }>) {
        try {
          await api.projects.create({
            name: old.name,
            description: old.description,
            sessionId: old.sessionId,
          });
        } catch {
          // Skip duplicates or errors
        }
      }

      // Clear old workspace-state projects
      await api.workspaceState.set('projects', []).catch(() => {});
      await refreshProjects();
    }).catch(() => {});
  }, [api, migrated, refreshProjects, isElectron]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setIsCreating(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setIsCreating(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleCreate = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;

    await createProject(newName.trim(), newDesc.trim() || undefined, sessionId ?? undefined);
    setNewName('');
    setNewDesc('');
    setIsCreating(false);
    setIsOpen(false);
  }, [newName, newDesc, sessionId, createProject]);

  const handleSwitch = useCallback(async (project: Project) => {
    setActiveProject(project.id);
    setIsOpen(false);
  }, [setActiveProject]);

  const handleDelete = useCallback(async (id: string) => {
    await deleteProject(id);
  }, [deleteProject]);

  return (
    <div className="project-picker" ref={dropdownRef}>
      <button
        className="project-picker__trigger"
        onClick={() => setIsOpen((v) => !v)}
        title="Switch project"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span className="project-picker__name">
          {activeProject?.name ?? 'No Project'}
        </span>
        <span className="project-picker__caret">{isOpen ? '\u25B4' : '\u25BE'}</span>
      </button>

      {isOpen && (
        <div className="project-picker__dropdown">
          <div className="project-picker__list">
            {projects.length === 0 && (
              <div className="project-picker__empty">No projects yet</div>
            )}
            {projects.map((project) => (
              <div
                key={project.id}
                className={`project-picker__item ${project.id === activeProjectId ? 'project-picker__item--active' : ''}`}
              >
                <button
                  className="project-picker__item-btn"
                  onClick={() => handleSwitch(project)}
                >
                  <span className="project-picker__item-name">{project.name}</span>
                  {project.description && (
                    <span className="project-picker__item-desc">{project.description}</span>
                  )}
                </button>
                <button
                  className="project-picker__item-delete"
                  onClick={() => handleDelete(project.id)}
                  title="Delete project"
                >
                  {'\u00D7'}
                </button>
              </div>
            ))}
          </div>

          {isCreating ? (
            <form className="project-picker__create-form" onSubmit={handleCreate}>
              <input
                className="project-picker__create-input"
                type="text"
                placeholder="Project name..."
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                autoFocus
              />
              <input
                className="project-picker__create-input"
                type="text"
                placeholder="Description (optional)"
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
              />
              <div className="project-picker__create-actions">
                <button className="project-picker__create-submit" type="submit">Create</button>
                <button
                  className="project-picker__create-cancel"
                  type="button"
                  onClick={() => setIsCreating(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <button
              className="project-picker__new-btn"
              onClick={() => setIsCreating(true)}
            >
              + New Project
            </button>
          )}
        </div>
      )}
    </div>
  );
}
